/**
 * Unified Transactions Dashboard
 * Single source of truth for ALL sales across all touchpoints
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, TrendingUp, ShoppingBag, Coffee, RefreshCw, 
  ArrowUpRight, ArrowDownRight, Clock, Calendar, Filter,
  Download, BarChart3, PieChart, AlertTriangle, CheckCircle,
  Smartphone, Monitor, Tablet, Globe, Phone, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Source icons
const SOURCE_ICONS = {
  pos: Monitor,
  online: Globe,
  kiosk: Tablet,
  server_tablet: Smartphone,
  phone: Phone,
  whatsapp: MessageCircle,
};

// Source colors
const SOURCE_COLORS = {
  pos: 'bg-blue-500',
  online: 'bg-green-500',
  kiosk: 'bg-purple-500',
  server_tablet: 'bg-orange-500',
  phone: 'bg-pink-500',
  whatsapp: 'bg-emerald-500',
};

export default function UnifiedTransactionsTab() {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [bySource, setBySource] = useState(null);
  const [byPayment, setByPayment] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, summaryRes, sourceRes, paymentRes, healthRes] = await Promise.all([
        api.get('/admin/transactions/stats'),
        api.get('/admin/transactions/summary'),
        api.get('/admin/transactions/by-source'),
        api.get('/admin/transactions/by-payment-method'),
        api.get('/admin/transactions/health'),
      ]);

      setStats(statsRes.data);
      setSummary(summaryRes.data);
      setBySource(sourceRes.data);
      setByPayment(paymentRes.data);
      setHealth(healthRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load transaction data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/admin/transactions/migrate');
      toast.success('Migration complete', {
        description: res.data.message,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    }
    setMigrating(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="unified-transactions-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Unified Sales</h2>
          <p className="text-muted-foreground text-sm">
            Single source of truth for ALL sales from every touchpoint
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Health Banner */}
      {health && !health.sync?.is_synced && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-800 dark:text-amber-300">
                  Data Sync Required ({health.sync?.percentage}% synced)
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  {health.sync?.missing} records need to be migrated to the unified transactions system.
                </p>
                <Button
                  size="sm"
                  className="mt-3 bg-amber-600 hover:bg-amber-700"
                  onClick={handleMigrate}
                  disabled={migrating}
                  data-testid="migrate-btn"
                >
                  {migrating ? 'Migrating...' : 'Sync All Data'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {health && health.sync?.is_synced && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <h3 className="font-medium text-green-800 dark:text-green-300">
                  All Systems Synced
                </h3>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {health.collections?.transactions} transactions from all touchpoints
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${stats.today?.revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stats.today?.transactions || 0} transactions
                  </p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">This Week</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${stats.this_week?.revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stats.this_week?.transactions || 0} transactions
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${stats.this_month?.revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stats.this_month?.transactions || 0} transactions
                  </p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">All Time</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${stats.all_time?.revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stats.all_time?.transactions || 0} transactions
                  </p>
                </div>
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <BarChart3 className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Financial Breakdown */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-green-600" />
              Financial Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Gross Revenue</p>
                <p className="text-xl font-bold text-foreground">
                  ${summary.gross_revenue?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Net Revenue</p>
                <p className="text-xl font-bold text-foreground">
                  ${summary.net_revenue?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-muted-foreground">(excl. 12.5% VAT)</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">VAT Collected</p>
                <p className="text-xl font-bold text-foreground">
                  ${summary.tax_collected?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Royalty Due (4%)</p>
                <p className="text-xl font-bold text-foreground">
                  ${summary.royalty_due?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-foreground">{summary.order_count || 0} Orders</p>
                  <p className="text-xs text-muted-foreground">Food &amp; drinks</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <Coffee className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-foreground">{summary.coffee_pass_count || 0} Coffee Passes</p>
                  <p className="text-xs text-muted-foreground">Subscriptions</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue by Source */}
      {bySource && bySource.breakdown?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-600" />
              Revenue by Touchpoint
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bySource.breakdown.map((item) => {
                const Icon = SOURCE_ICONS[item.source] || Monitor;
                const colorClass = SOURCE_COLORS[item.source] || 'bg-gray-500';
                return (
                  <div key={item.source} className="flex items-center gap-3">
                    <div className={`p-2 ${colorClass} rounded-lg`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-foreground capitalize">
                          {item.source?.replace(/_/g, ' ') || 'Unknown'}
                        </span>
                        <span className="font-bold text-foreground">
                          ${item.gross_revenue?.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-muted-foreground">
                        <span>{item.transaction_count} transactions</span>
                        <span>{item.percentage}%</span>
                      </div>
                      <div className="mt-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full ${colorClass}`}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue by Payment Method */}
      {byPayment && byPayment.breakdown?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Revenue by Payment Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {byPayment.breakdown.map((item) => (
                <div key={item.payment_method} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-foreground capitalize">
                        {item.payment_method?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                      <span className="font-bold text-foreground">
                        ${item.gross_revenue?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>{item.transaction_count} transactions</span>
                      <span>{item.percentage}%</span>
                    </div>
                    <div className="mt-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Sources Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">About Unified Transactions</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            This dashboard consolidates ALL sales from every touchpoint into a single view:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>POS</strong> - Main register sales</li>
            <li><strong>Kiosk</strong> - Self-service kiosk orders</li>
            <li><strong>Online</strong> - Website ordering</li>
            <li><strong>Server Tablet</strong> - Tableside orders</li>
            <li><strong>Phone</strong> - Voice orders (AI assistant)</li>
            <li><strong>WhatsApp</strong> - Messaging orders</li>
            <li><strong>Coffee Pass</strong> - Subscription sales</li>
          </ul>
          <p className="mt-4">
            All financial reports now pull from this unified <code className="bg-muted px-1 rounded">transactions</code> collection
            instead of querying multiple separate databases.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
